import React, { useState, useEffect, useRef, useCallback } from 'react';
import './VehicleManagement.css';
import { getVehicles, createVehicle, updateVehicle, deleteVehicle, saveVehicleDocuments, getVehicleById, getVehicleDocuments } from '../../services/vehiclesService';
import { getAgencies } from '../../services/agenciesService';
import { getDrivers } from '../../services/driversService';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';

const VehicleManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [documentValidityFilter, setDocumentValidityFilter] = useState('');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [agencyDropdownOpen, setAgencyDropdownOpen] = useState(false);
  const [documentDropdownOpen, setDocumentDropdownOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState('add'); // 'add' or 'update'
  const [successVehicleId, setSuccessVehicleId] = useState('');
  const [successVehicleNumber, setSuccessVehicleNumber] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'photo' or 'document'
  const [deleteDocumentKey, setDeleteDocumentKey] = useState(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [saveConfirmType, setSaveConfirmType] = useState(null); // 'add' or 'update'
  const [showDeleteVehicleConfirm, setShowDeleteVehicleConfirm] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deletedVehicleInfo, setDeletedVehicleInfo] = useState(null);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedVehicleForDocs, setSelectedVehicleForDocs] = useState(null);
  const [activeDocTab, setActiveDocTab] = useState('rcBook');
  const [documentDetails, setDocumentDetails] = useState({
    rcBook: { expiryDate: '', status: 'Valid', lastUpdated: '' },
    insurance: { expiryDate: '', status: 'Valid', lastUpdated: '' },
    pollutionCertificate: { expiryDate: '', status: 'Valid', lastUpdated: '' },
    fitnessCertificate: { expiryDate: '', status: 'Valid', lastUpdated: '' },
    permit: { expiryDate: '', status: 'Valid', lastUpdated: '' },
    roadTax: { expiryDate: '', status: 'Valid', lastUpdated: '' }
  });
  const [documentPreviews, setDocumentPreviews] = useState({
    rcBook: null,
    insurance: null,
    pollutionCertificate: null,
    fitnessCertificate: null,
    permit: null,
    roadTax: null
  });
  const [documentFiles, setDocumentFiles] = useState({
    rcBook: null,
    insurance: null,
    pollutionCertificate: null,
    fitnessCertificate: null,
    permit: null,
    roadTax: null
  });
  const [documentFileNames, setDocumentFileNames] = useState({
    rcBook: null,
    insurance: null,
    pollutionCertificate: null,
    fitnessCertificate: null,
    permit: null,
    roadTax: null
  });
  const [originalDocumentFileNames, setOriginalDocumentFileNames] = useState({});
  const [showDocumentSaveConfirm, setShowDocumentSaveConfirm] = useState(false);
  const [showDocumentSuccess, setShowDocumentSuccess] = useState(false);
  const [uploadedDocumentNames, setUploadedDocumentNames] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const [showImportSuccess, setShowImportSuccess] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState(0);
  const [importSkippedCount, setImportSkippedCount] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSampleCSVModal, setShowSampleCSVModal] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importValidVehicles, setImportValidVehicles] = useState([]);
  const [importInvalidRows, setImportInvalidRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [agenciesList, setAgenciesList] = useState([]); // For form dropdown (from API)
  const [agencies, setAgencies] = useState([]); // For filter dropdown (from vehicles)
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [showDuplicateErrorModal, setShowDuplicateErrorModal] = useState(false);

  const DOCUMENT_FIELD_MAP = {
    rcBook: 'rc_book_number',
    insurance: 'insurance_number',
    pollutionCertificate: 'pollution_certificate_number',
    fitnessCertificate: 'fitness_certificate_number',
    permit: 'permit_number',
    roadTax: 'road_tax_number'
  };

  const createEmptyDocument = () => ({
    file: null,
    fileName: null,
    preview: null,
    date: '',
    documentNumber: ''
  });

  // Map API response (snake_case) to component format (camelCase)
  const mapVehicleFromAPI = (apiVehicle) => {
    // Normalize documents: API may return an array of rows or an object.
    let documentsFromApi = {};
    if (Array.isArray(apiVehicle.documents)) {
      documentsFromApi = {};
      apiVehicle.documents.forEach((d) => {
        const key = findDocKeyByType?.(d.document_type) || d.document_type;
        if (!key) return;
        documentsFromApi[key] = {
          file: null,
          fileName: d.file_name || null,
          preview: d.document_url || null,
          date: d.expiry_date || '',
          documentNumber: d.document_number || ''
        };
      });
    } else {
      documentsFromApi = apiVehicle.documents ? { ...apiVehicle.documents } : {};
    }

    Object.entries(DOCUMENT_FIELD_MAP).forEach(([key, apiField]) => {
      if (!documentsFromApi[key]) {
        documentsFromApi[key] = createEmptyDocument();
      }
      if (!documentsFromApi[key].documentNumber) {
        documentsFromApi[key].documentNumber = apiVehicle[apiField] || '';
      }
    });

    // Extract agency name from nested agency object or fallback to agency_name/agency_id
    const agencyName = apiVehicle.agency?.agency_name || 
                      apiVehicle.agency_name || 
                      (apiVehicle.agency_id ? apiVehicle.agency_id.toString() : '') || 
                      '';

    // Extract driver name from nested driver object or fallback to driver_name/assigned_driver_id
    const driverName = apiVehicle.driver?.full_name || 
                       apiVehicle.driver_name || 
                       (apiVehicle.assigned_driver_id ? apiVehicle.assigned_driver_id.toString() : '') || 
                       'Not Assigned';

    return {
      // primaryId keeps the original numeric/db id returned by the API when available.
      primaryId: apiVehicle.vehicle_id || apiVehicle.id || null,
      // id is used for display/unique key in the UI (kept as string for consistency)
      id: (apiVehicle.vehicle_id || apiVehicle.id)?.toString() || '',
      vehicleNumber: apiVehicle.vehicle_number || '',
      vehicleType: apiVehicle.vehicle_type || '',
      capacity: apiVehicle.capacity_kg || 0,
      agency: agencyName,
      agencyId: apiVehicle.agency_id || apiVehicle.agency?.id,
      status: apiVehicle.status || 'Active',
      assignedDriver: driverName,
      assignedDriverId: apiVehicle.assigned_driver_id || apiVehicle.driver?.id,
      photo: apiVehicle.photo_url || null,
      rc_book_number: apiVehicle.rc_book_number || '',
      insurance_number: apiVehicle.insurance_number || '',
      pollution_certificate_number: apiVehicle.pollution_certificate_number || '',
      fitness_certificate_number: apiVehicle.fitness_certificate_number || '',
      permit_number: apiVehicle.permit_number || '',
      road_tax_number: apiVehicle.road_tax_number || '',
      documents: documentsFromApi,
      // documentsExpiry: apiVehicle.documents_expiry || { total: 7, valid: 7, expired: 0 },
      createdAt: apiVehicle.created_at,
      updatedAt: apiVehicle.updated_at
    };
  };

  // Map component format (camelCase) to API format (snake_case)
  const mapVehicleToAPI = (vehicle) => {
    const payload = {
      vehicle_number: vehicle.vehicleNumber,
      vehicle_type: vehicle.vehicleType,
      capacity_kg: vehicle.capacity,
      agency_id: vehicle.agencyId || null, // Only send integer ID, not string name
      assigned_driver_id: vehicle.assignedDriverId || null,
      status: vehicle.status,
      rc_book_number: vehicle.rc_book_number || '',
      insurance_number: vehicle.insurance_number || '',
      pollution_certificate_number: vehicle.pollution_certificate_number || '',
      fitness_certificate_number: vehicle.fitness_certificate_number || '',
      permit_number: vehicle.permit_number || '',
      road_tax_number: vehicle.road_tax_number || ''
    };
    
    // Only include photo if it's explicitly set (File object for new/updated photo, or null for removed photo)
    // If undefined, don't include it (photo not changed)
    if (vehicle.photo !== undefined) {
      payload.photo = vehicle.photo; // Send the actual File object or null, not photo_url
    }

    Object.entries(DOCUMENT_FIELD_MAP).forEach(([key, apiField]) => {
      const value = vehicle.documents?.[key]?.documentNumber?.trim();
      if (value) {
        payload[apiField] = value;
      }
    });

    return {
      ...payload
    };
  };

  // Fetch vehicles from API
  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      // Convert agency filter (name) to agency_id for API
      if (agencyFilter) {
        // If it's a number, treat as agency_id directly
        if (!isNaN(agencyFilter)) {
          params.agency_id = parseInt(agencyFilter);
        } else {
          // If it's a name, find the matching agency ID from agenciesList
          const matchedAgency = agenciesList.find(a => 
            (a.name || '').trim().toLowerCase() === agencyFilter.trim().toLowerCase()
          );
          if (matchedAgency && matchedAgency.id) {
            params.agency_id = matchedAgency.id;
          } else {
            // Fallback: use search if agency name not found
            // This handles edge cases where agency name might not match exactly
            console.warn(`Agency "${agencyFilter}" not found in agencies list, using search fallback`);
          }
        }
      }

      const response = await getVehicles(params);
      
      // API response structure: { success, message, data, pagination }
      const vehiclesData = response.data || [];
      const pagination = response.pagination || {};
      
      // Map API vehicles to component format
      const mappedVehicles = vehiclesData.map(mapVehicleFromAPI);
      setVehicles(mappedVehicles);
      setTotalCount(pagination.total || 0);

      // Fetch documents for each vehicle in parallel and merge into vehicle list so table shows expiry counts
      try {
        const docsResponses = await Promise.all(mappedVehicles.map(async (v) => {
          const vid = v.primaryId ?? v.id;
          if (!vid) return { id: v.id, docs: [] };
          const docs = await getVehicleDocuments(vid);
          return { id: v.id, docs };
        }));

        // Build a map vehicleId -> docs array
        const vehicleIdToDocs = {};
        docsResponses.forEach(r => {
          vehicleIdToDocs[r.id] = r.docs || [];
        });

        // Merge into vehicles state
        const merged = mappedVehicles.map(v => {
          const docsArr = vehicleIdToDocs[v.id] || [];
          if (!docsArr || docsArr.length === 0) return v;
          const mappedDocs = {};
          Object.keys(documentDetails).forEach(k => {
            mappedDocs[k] = { file: null, fileName: null, preview: null, date: '', documentNumber: '' };
          });
          docsArr.forEach(d => {
            const key = findDocKeyByType(d.document_type) || d.document_type;
            if (!key) return;
            mappedDocs[key] = {
              file: null,
              fileName: d.file_name || null,
              preview: d.document_url || null,
              date: d.expiry_date || '',
              documentNumber: d.document_number || '',
              updatedAt: d.updated_at || d.updatedAt || null
            };
          });
          return {
            ...v,
            documents: mappedDocs
          };
        });
        setVehicles(merged);
      } catch (err) {
        console.warn('Failed to fetch per-vehicle documents for table:', err);
      }
      
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError(err.message || 'Failed to fetch vehicles. Please try again.');
      setVehicles([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, statusFilter, agencyFilter, searchQuery]);

  // Fetch vehicles on mount and when pagination/filters change
  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Debounced search - reset to page 1 when search query changes
  useEffect(() => {
    if (searchQuery && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery]);

  // Fetch agencies for dropdown
  const fetchAgencies = useCallback(async () => {
    try {
      const response = await getAgencies({ page: 1, limit: 1000 }); // Fetch all agencies for dropdown
      
      // Handle different response structures
      let agenciesData = [];
      if (Array.isArray(response.data)) {
        agenciesData = response.data;
      } else if (response.data && Array.isArray(response.data.items)) {
        agenciesData = response.data.items;
      } else if (Array.isArray(response)) {
        agenciesData = response;
      } else {
        agenciesData = response.data?.items || response.items || [];
      }
      
      // Ensure agenciesData is an array
      if (!Array.isArray(agenciesData)) {
        agenciesData = [];
      }
      
      // Map agencies to format: { id, name }
      const mappedAgencies = agenciesData.map(agency => ({
        id: agency.id,
        name: agency.agency_name || agency.name || ''
      })).filter(agency => agency.id && agency.name);
      
      setAgenciesList(mappedAgencies);
    } catch (err) {
      console.error('Error fetching agencies:', err);
      // Don't set error state here, just log it - agencies dropdown will be empty
      setAgencies([]);
    }
  }, []);

  // Fetch agencies on component mount
  useEffect(() => {
    fetchAgencies();
  }, [fetchAgencies]);

  // Fetch drivers for dropdown
  const fetchDrivers = useCallback(async () => {
    try {
      const response = await getDrivers({ page: 1, limit: 1000 }); // Fetch all drivers for dropdown
      
      // Handle different response structures
      let driversData = [];
      if (response.data && Array.isArray(response.data.items)) {
        driversData = response.data.items;
      } else if (Array.isArray(response.data)) {
        driversData = response.data;
      } else if (Array.isArray(response)) {
        driversData = response;
      } else {
        driversData = response.data?.items || response.items || [];
      }
      
      // Ensure driversData is an array
      if (!Array.isArray(driversData)) {
        driversData = [];
      }
      
      // Map drivers to format: { id, name, driver_id, agency_id }
      const mappedDrivers = driversData.map(driver => ({
        id: driver.id,
        driver_id: driver.driver_id || driver.id,
        name: driver.full_name || driver.name || '',
        mobile_number: driver.mobile_number || '',
        agency_id: driver.agency_id || driver.agency?.id || null,
        status: driver.driver_status || driver.status || 'Active'
      })).filter(driver => driver.id && driver.name && driver.status === 'Active'); // Only show active drivers
      
      setDrivers(mappedDrivers);
    } catch (err) {
      console.error('Error fetching drivers:', err);
      // Don't set error state here, just log it - drivers dropdown will be empty
      setDrivers([]);
    }
  }, []);

  // Fetch drivers on component mount
  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);
 

  const statusDropdownRef = useRef(null);
  const agencyDropdownRef = useRef(null);
  const documentDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    vehicleNumber: '',
    vehicleType: '',
    capacity: '',
    agency: '',
    agencyId: null,
    assignedDriver: '',
    assignedDriverId: null,
    status: '',
    photo: null,
    photoPreview: null,
    documents: {
      rcBook: { file: null, date: '', documentNumber: '' },
      insurance: { file: null, date: '', documentNumber: '' },
      pollutionCertificate: { file: null, date: '', documentNumber: '' },
      fitnessCertificate: { file: null, date: '', documentNumber: '' },
      permit: { file: null, date: '', documentNumber: '' },
      roadTax: { file: null, date: '', documentNumber: '' }
    }
  });

  // Compute filtered drivers for Assigned Driver dropdown:
  // - only drivers belonging to selected agency (if an agency is selected)
  // - exclude drivers who are already assigned to other vehicles
  // - allow the currently selected/assigned driver for the vehicle being edited
  useEffect(() => {
    try {
      const assignedIds = new Set();
      vehicles.forEach((v) => {
        if (v.assignedDriverId) {
          assignedIds.add(String(v.assignedDriverId));
        }
      });

      // If editing an existing vehicle, allow its currently assigned driver to remain selectable
      if (editingVehicle && (editingVehicle.assignedDriverId || editingVehicle.assignedDriverId === 0)) {
        assignedIds.delete(String(editingVehicle.assignedDriverId));
      }

      const filtered = drivers.filter((d) => {
        // If agency is selected, filter by agency_id when available
        if (formData.agencyId && d.agency_id && String(d.agency_id) !== String(formData.agencyId)) {
          return false;
        }

        // Always allow the currently selected driver in the form (so editing doesn't remove it)
        if (formData.assignedDriverId && String(d.id) === String(formData.assignedDriverId)) {
          return true;
        }

        // Exclude drivers that are already assigned to other vehicles
        if (assignedIds.has(String(d.id))) {
          return false;
        }

        return true;
      });

      setFilteredDrivers(filtered);
    } catch (err) {
      // On error, fallback to showing all drivers
      setFilteredDrivers(drivers);
    }
  }, [drivers, vehicles, formData.agencyId, formData.assignedDriverId, editingVehicle]);

  // Initialize form with vehicle data for editing
  const initializeEditForm = (vehicle) => {
    // Convert saved documents to form format
    const formDocuments = vehicle.documents || {};
    const documents = {
      rcBook: { 
        file: null, 
        fileName: formDocuments.rcBook?.fileName || null,
        preview: formDocuments.rcBook?.preview || null,
        date: formatDateForInput(formDocuments.rcBook?.date) || '',
        documentNumber: formDocuments.rcBook?.documentNumber || ''
      },
      insurance: { 
        file: null, 
        fileName: formDocuments.insurance?.fileName || null,
        preview: formDocuments.insurance?.preview || null,
        date: formatDateForInput(formDocuments.insurance?.date) || '',
        documentNumber: formDocuments.insurance?.documentNumber || ''
      },
      pollutionCertificate: { 
        file: null, 
        fileName: formDocuments.pollutionCertificate?.fileName || null,
        preview: formDocuments.pollutionCertificate?.preview || null,
        date: formatDateForInput(formDocuments.pollutionCertificate?.date) || '',
        documentNumber: formDocuments.pollutionCertificate?.documentNumber || ''
      },
      fitnessCertificate: { 
        file: null, 
        fileName: formDocuments.fitnessCertificate?.fileName || null,
        preview: formDocuments.fitnessCertificate?.preview || null,
        date: formatDateForInput(formDocuments.fitnessCertificate?.date) || '',
        documentNumber: formDocuments.fitnessCertificate?.documentNumber || ''
      },
      permit: { 
        file: null, 
        fileName: formDocuments.permit?.fileName || null,
        preview: formDocuments.permit?.preview || null,
        date: formatDateForInput(formDocuments.permit?.date) || '',
        documentNumber: formDocuments.permit?.documentNumber || ''
      },
      roadTax: { 
        file: null, 
        fileName: formDocuments.roadTax?.fileName || null,
        preview: formDocuments.roadTax?.preview || null,
        date: formatDateForInput(formDocuments.roadTax?.date) || '',
        documentNumber: formDocuments.roadTax?.documentNumber || ''
      }
    };

    setFormData({
      vehicleNumber: vehicle.vehicleNumber || '',
      vehicleType: vehicle.vehicleType || '',
      capacity: vehicle.capacity || '',
      agency: vehicle.agency || '',
      agencyId: vehicle.agencyId || null,
      assignedDriver: vehicle.assignedDriver || '',
      assignedDriverId: vehicle.assignedDriverId || null,
      status: vehicle.status || '',
      photo: null,
      photoPreview: vehicle.photo || null,
      documents: documents
    });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({...formData, photo: file, photoPreview: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentUpload = (docKey, file) => {
    const newDocuments = {...formData.documents};
    newDocuments[docKey].file = file;
    newDocuments[docKey].fileName = file ? file.name : null;
    
    // Create preview for images
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updatedDocuments = {...formData.documents};
        updatedDocuments[docKey].file = file;
        updatedDocuments[docKey].fileName = file.name;
        updatedDocuments[docKey].preview = reader.result;
        setFormData({...formData, documents: updatedDocuments});
      };
      reader.readAsDataURL(file);
    } else if (file) {
      setFormData({...formData, documents: newDocuments});
    }
  };

  const handleDocumentRemove = (docKey, inputId) => {
    const newDocuments = {...formData.documents};
    newDocuments[docKey].file = null;
    newDocuments[docKey].fileName = null;
    newDocuments[docKey].preview = null;
    setFormData({...formData, documents: newDocuments});
    // Clear the file input
    const fileInput = document.getElementById(inputId);
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleDeleteClick = (type, docKey = null, inputId = null) => {
    setDeleteType(type);
    setDeleteDocumentKey(docKey);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteType === 'photo') {
      // Set photo to null and photoPreview to null to indicate photo was removed
      setFormData({...formData, photo: null, photoPreview: null});
      document.getElementById('edit-photo-input').value = '';
    } else if (deleteType === 'document' && deleteDocumentKey) {
      const inputId = `edit-${deleteDocumentKey}-file`;
      handleDocumentRemove(deleteDocumentKey, inputId);
    }
    setShowDeleteConfirm(false);
    setDeleteType(null);
    setDeleteDocumentKey(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteType(null);
    setDeleteDocumentKey(null);
  };

  const handleVehicleDeleteClick = (vehicle) => {
    setVehicleToDelete(vehicle);
    setShowDeleteVehicleConfirm(true);
  };

  const handleVehicleDeleteConfirm = async () => {
    if (vehicleToDelete) {
      try {
        // Store deleted vehicle info for success modal
        const deletedInfo = {
          id: vehicleToDelete.id,
          number: vehicleToDelete.vehicleNumber
        };
        
        // Delete vehicle via API
        await deleteVehicle(vehicleToDelete.id);
        
        // Close confirmation and show success
        setShowDeleteVehicleConfirm(false);
        setVehicleToDelete(null);
        setDeletedVehicleInfo(deletedInfo);
        setShowDeleteSuccess(true);
        
        // Refresh vehicle list
        await fetchVehicles();
        
        // If current page becomes empty, go to previous page
        if (vehicles.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } catch (err) {
        console.error('Error deleting vehicle:', err);
        setError(err.message || 'Failed to delete vehicle. Please try again.');
        setShowDeleteVehicleConfirm(false);
      }
    }
  };

  const handleVehicleDeleteCancel = () => {
    setShowDeleteVehicleConfirm(false);
    setVehicleToDelete(null);
  };

  const handleImportClick = async (vehicle) => {
    // Refresh vehicle from API to get latest documents (use primaryId if available)
    const vehicleId = vehicle.primaryId ?? vehicle.id;
    setActiveDocTab('rcBook'); // Reset to first tab
    setSelectedVehicleForDocs(vehicle); // set immediately so modal header shows while fetching

    try {
      if (vehicleId) {
        const res = await getVehicleById(vehicleId, true);
        let freshVehicle = res?.data || res;
        // If vehicle endpoint doesn't include documents, fetch them separately
        if (!freshVehicle?.documents || (Array.isArray(freshVehicle.documents) && freshVehicle.documents.length === 0)) {
          const docs = await getVehicleDocuments(vehicleId);
          freshVehicle = { ...(freshVehicle || {}), documents: docs };
        }
        const sourceVehicle = freshVehicle || vehicle;

        // Load document details from latest vehicle
      if (sourceVehicle.documents) {
          // sourceVehicle.documents may be an array of document rows; map into our keyed shape
          const docsArray = Array.isArray(sourceVehicle.documents) ? sourceVehicle.documents : Object.values(sourceVehicle.documents);
          const mappedDocs = {};
          const loadedDetails = {};
          const loadedPreviews = {};
          const loadedFileNames = {};
          const originalFileNames = {};
          docsArray.forEach((d) => {
            const key = findDocKeyByType(d.document_type) || d.document_type || null;
            if (!key) return;
            mappedDocs[key] = {
              id: d.id,
              documentNumber: d.document_number || '',
              fileName: d.file_name || null,
              preview: d.document_url ? encodeURI(d.document_url) : (d.preview ? encodeURI(d.preview) : null),
              date: d.expiry_date || d.date || '',
              updatedAt: d.updated_at || d.updatedAt || null
            };
            loadedDetails[key] = {
              expiryDate: formatDateForInput(mappedDocs[key].date) || '',
              status: mappedDocs[key].fileName ? 'Valid' : 'Missing',
              lastUpdated: mappedDocs[key].updatedAt ? new Date(mappedDocs[key].updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''
            };
            loadedPreviews[key] = mappedDocs[key].preview || null;
            loadedFileNames[key] = mappedDocs[key].fileName || null;
            if (mappedDocs[key].fileName) originalFileNames[key] = mappedDocs[key].fileName;
          });
          // ensure all keys exist
          Object.keys(documentDetails).forEach(k => {
            if (!mappedDocs[k]) {
              mappedDocs[k] = { id: null, documentNumber: '', fileName: null, preview: null, date: '', updatedAt: null };
              if (!loadedDetails[k]) {
                loadedDetails[k] = { expiryDate: '', status: 'Missing', lastUpdated: '' };
              }
            }
          });
          setDocumentDetails(loadedDetails);
          setDocumentPreviews(loadedPreviews);
          setDocumentFileNames(loadedFileNames);
          setOriginalDocumentFileNames(originalFileNames);
          setSelectedVehicleForDocs(prev => ({ ...prev, documents: mappedDocs }));
        } else {
          // reset defaults
          setDocumentDetails({
            rcBook: { expiryDate: '', status: 'Missing', lastUpdated: '' },
            insurance: { expiryDate: '', status: 'Missing', lastUpdated: '' },
            pollutionCertificate: { expiryDate: '', status: 'Missing', lastUpdated: '' },
            fitnessCertificate: { expiryDate: '', status: 'Missing', lastUpdated: '' },
            permit: { expiryDate: '', status: 'Missing', lastUpdated: '' },
            roadTax: { expiryDate: '', status: 'Missing', lastUpdated: '' }
          });
          setDocumentPreviews({
            rcBook: null,
            insurance: null,
            pollutionCertificate: null,
            fitnessCertificate: null,
            permit: null,
            roadTax: null
          });
          setDocumentFileNames({
            rcBook: null,
            insurance: null,
            pollutionCertificate: null,
            fitnessCertificate: null,
            permit: null,
            roadTax: null
          });
          setOriginalDocumentFileNames({});
        }
      } else {
        // no numeric id - fallback to local vehicle object
        setSelectedVehicleForDocs(vehicle);
        setDocumentDetails({
          rcBook: { expiryDate: '', status: 'Missing', lastUpdated: '' },
          insurance: { expiryDate: '', status: 'Missing', lastUpdated: '' },
          pollutionCertificate: { expiryDate: '', status: 'Missing', lastUpdated: '' },
          fitnessCertificate: { expiryDate: '', status: 'Missing', lastUpdated: '' },
          permit: { expiryDate: '', status: 'Missing', lastUpdated: '' },
          roadTax: { expiryDate: '', status: 'Missing', lastUpdated: '' }
        });
      }
    } catch (err) {
      console.error('Failed to fetch vehicle documents:', err);
      // keep existing selectedVehicleForDocs but use its documents if available
      if (vehicle.documents) {
        const loadedDetails = {};
        const loadedPreviews = {};
        const loadedFileNames = {};
        const originalFileNames = {};
        Object.keys(documentDetails).forEach(key => {
          loadedDetails[key] = {
            expiryDate: vehicle.documents[key]?.date || '',
            status: vehicle.documents[key]?.fileName ? 'Valid' : 'Missing',
            lastUpdated: vehicle.documents[key]?.updatedAt ? new Date(vehicle.documents[key].updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : (vehicle.documents[key]?.date ? new Date(vehicle.documents[key].date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '')
          };
          if (vehicle.documents[key]?.preview) {
            loadedPreviews[key] = vehicle.documents[key].preview;
          }
          if (vehicle.documents[key]?.fileName) {
            loadedFileNames[key] = vehicle.documents[key].fileName;
            originalFileNames[key] = vehicle.documents[key].fileName;
          }
        });
        setDocumentDetails(loadedDetails);
        setDocumentPreviews(loadedPreviews);
        setDocumentFileNames(loadedFileNames);
        setOriginalDocumentFileNames(originalFileNames);
      }
    }
    setShowDocumentsModal(true);
  };

  const handleDocumentReplace = (docKey) => {
    const inputId = `doc-replace-${docKey}`;
    document.getElementById(inputId).click();
  };

  const handleDocumentFileChange = (docKey, file) => {
    if (file) {
      // Store file name
      setDocumentFileNames(prev => ({
        ...prev,
        [docKey]: file.name
      }));
      // Store actual file object for upload
      setDocumentFiles(prev => ({
        ...prev,
        [docKey]: file
      }));

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setDocumentPreviews(prev => ({
            ...prev,
            [docKey]: reader.result
          }));
        };
        reader.readAsDataURL(file);
      }
      
      // Update last updated date
      setDocumentDetails(prev => ({
        ...prev,
        [docKey]: {
          ...prev[docKey],
          status: 'Valid',
          lastUpdated: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        }
      }));
    }
  };

  const handleDocumentDownload = (docKey) => {
    // Placeholder for download functionality
    alert(`Downloading ${docKey} document...`);
  };

  const getDocumentLabel = (key) => {
    const labels = {
      rcBook: 'RC Book',
      insurance: 'Insurance Certificate',
      pollutionCertificate: 'PUC',
      fitnessCertificate: 'Fitness Certificate',
      permit: 'Permit',
      roadTax: 'Road Tax Receipt'
    };
    return labels[key] || key;
  };
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  // Map UI keys to backend document_type labels
  const DOCUMENT_TYPE_LABEL_MAP = {
    rcBook: 'RC Book',
    insurance: 'Insurance Certificate',
    pollutionCertificate: 'PUC',
    fitnessCertificate: 'Fitness Certificate',
    permit: 'Permit',
    roadTax: 'Road Tax Receipt'
  };
  // Reverse map: match backend document_type to our UI key
  const findDocKeyByType = (docType) => {
    if (!docType) return null;
    const normalized = String(docType).toLowerCase();
    if (normalized.includes('rc')) return 'rcBook';
    if (normalized.includes('insurance')) return 'insurance';
    if (normalized.includes('puc') || normalized.includes('pollution')) return 'pollutionCertificate';
    if (normalized.includes('fitness')) return 'fitnessCertificate';
    if (normalized.includes('permit')) return 'permit';
    if (normalized.includes('road') || normalized.includes('tax')) return 'roadTax';
    return null;
  };

  const handleDocumentSave = async () => {
    try {
      // Determine vehicle primary id
      const vehicleId = selectedVehicleForDocs?.primaryId ?? selectedVehicleForDocs?.id;
      
      // Fetch existing documents first to get their IDs for proper updates
      let existingDocsMap = {};
      try {
        const existingDocs = await getVehicleDocuments(vehicleId);
        if (Array.isArray(existingDocs)) {
          existingDocs.forEach(doc => {
            const key = findDocKeyByType(doc.document_type);
            if (key && doc.id) {
              // Store by both key and document_type for matching
              if (!existingDocsMap[key]) {
                existingDocsMap[key] = doc;
              }
            }
          });
        }
      } catch (err) {
        console.warn('Failed to fetch existing documents, proceeding with current state:', err);
      }

      // Build documents payload for API using current documentDetails and file name/preview info
      // Only send changed documents to avoid creating duplicates.
      const changedKeys = Object.keys(documentDetails).filter((key) => {
        const existingDoc = selectedVehicleForDocs?.documents?.[key] || existingDocsMap[key] || {};
        const hasNewFile = Boolean(documentFiles[key] || (formData?.documents?.[key]?.file));
        const fileNameChanged = documentFileNames[key] && documentFileNames[key] !== existingDoc.fileName;
        const expiryChanged = (documentDetails[key]?.expiryDate || '') !== (existingDoc.date || formData?.documents?.[key]?.date || '');
        const numberChanged = (formData?.documents?.[key]?.documentNumber || existingDoc.documentNumber || '') !== (existingDoc.documentNumber || '');
        return hasNewFile || fileNameChanged || expiryChanged || numberChanged;
      });

      const docsPayload = changedKeys.map((key) => {
        // Check both selectedVehicleForDocs and existingDocsMap for the document ID
        const existingDoc = selectedVehicleForDocs?.documents?.[key] || {};
        const fetchedDoc = existingDocsMap[key] || {};
        // Use ID from fetched docs first (most up-to-date), then from existingDoc
        const docId = fetchedDoc.id || existingDoc.id || null;
        
        return {
          id: docId,
          document_type: getDocumentLabel(key),
          document_number: formData?.documents?.[key]?.documentNumber || existingDoc.documentNumber || '',
          expiry_date: documentDetails[key]?.expiryDate || formData?.documents?.[key]?.date || existingDoc.date || '',
          file_name: documentFileNames[key] || existingDoc.fileName || formData?.documents?.[key]?.fileName || null,
          file: documentFiles[key] || formData?.documents?.[key]?.file || null
        };
      });

      // Call API to save documents
      await saveVehicleDocuments(vehicleId, docsPayload);

      // Refresh vehicle from API and replace in vehicles list so we display stored data (ids, urls)
    try {
        // Prefer fetching documents directly first so we always load stored docs
        const docs = await getVehicleDocuments(vehicleId);
        // Then fetch vehicle main payload (if available) to map other fields
        const res = await getVehicleById(vehicleId, true);
        let apiVehicle = res?.data?.data || res?.data || res || {};
        if (!apiVehicle.documents || apiVehicle.documents.length === 0) {
          apiVehicle = { ...apiVehicle, documents: docs };
        }
        const mapped = mapVehicleFromAPI(apiVehicle);
        // Replace vehicle in list if present
        setVehicles(prev => prev.map(v => (v.id === mapped.id ? mapped : v)));
        // Update selected vehicle shown in modal
        setSelectedVehicleForDocs(mapped);
        
        // Update documentDetails with refreshed data including updated_at
        if (mapped.documents) {
          const updatedDetails = { ...documentDetails };
          Object.keys(documentDetails).forEach(key => {
            const doc = mapped.documents[key];
            if (doc) {
              updatedDetails[key] = {
                ...updatedDetails[key],
                expiryDate: formatDateForInput(doc.date) || updatedDetails[key].expiryDate || '',
                status: doc.fileName ? 'Valid' : 'Missing',
                lastUpdated: doc.updatedAt ? new Date(doc.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : (updatedDetails[key].lastUpdated || '')
              };
            }
          });
          setDocumentDetails(updatedDetails);
        }
      } catch (err) {
        console.warn('Failed to refresh vehicle after saving documents:', err);
      }
      setShowDocumentSaveConfirm(false);
      setShowDocumentsModal(false);
      setShowDocumentSuccess(true);
    } catch (err) {
      console.error('Error saving vehicle documents:', err);
      const errorMessage = err.message || 'Failed to save documents. Please try again.';
      
      // Check if it's a backend route issue
      if (errorMessage.includes('Backend route not found') || errorMessage.includes('routed to frontend')) {
        alert(`Backend Configuration Error:\n\n${errorMessage}\n\nPlease contact your backend developer to implement the PUT /api/vehicles/documents/:id route.`);
      } else {
        // Show detailed error message for debugging
        const detailedMessage = errorMessage.includes('Failed to upload') 
          ? `${errorMessage}\n\nThis usually indicates a backend error. Check:\n1. Backend server logs\n2. Database connection\n3. File upload configuration\n4. Required fields validation`
          : errorMessage;
        alert(detailedMessage);
      }
      setShowDocumentSaveConfirm(false);
    }
  };

  const handleDocumentSaveConfirm = () => {
    handleDocumentSave();
  };

  const handleDocumentSaveCancel = () => {
    setShowDocumentSaveConfirm(false);
    setUploadedDocumentNames([]);
  };

  const handleEditClick = (vehicle) => {
    setEditingVehicle(vehicle);
    initializeEditForm(vehicle);
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      vehicleNumber: '',
      vehicleType: '',
      capacity: '',
      agency: '',
      agencyId: null,
      assignedDriver: '',
      assignedDriverId: null,
      status: '',
      photo: null,
      photoPreview: null,
      documents: {
        rcBook: { file: null, date: '', documentNumber: '' },
        insurance: { file: null, date: '', documentNumber: '' },
        pollutionCertificate: { file: null, date: '', documentNumber: '' },
        fitnessCertificate: { file: null, date: '', documentNumber: '' },
        permit: { file: null, date: '', documentNumber: '' },
        roadTax: { file: null, date: '', documentNumber: '' }
      }
    });
    setFormErrors({});
    setEditingVehicle(null);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.vehicleNumber.trim()) {
      errors.vehicleNumber = 'Vehicle Number is required';
    }
    
    if (!formData.vehicleType) {
      errors.vehicleType = 'Vehicle Type is required';
    }
    
    if (!formData.capacity || formData.capacity <= 0) {
      errors.capacity = 'Capacity is required';
    }
    
    if (!formData.agency) {
      errors.agency = 'Transporter is required';
    }
    
    if (!formData.status) {
      errors.status = 'Status is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check for duplicate vehicle number
  const checkDuplicateVehicleNumber = (vehicleNumber, excludeVehicleId = null) => {
    if (!vehicleNumber || !vehicleNumber.trim()) {
      return false;
    }
    
    const normalizedInput = vehicleNumber.trim().toLowerCase();
    
    return vehicles.some(vehicle => {
      // Exclude the current vehicle being edited
      if (excludeVehicleId && (vehicle.primaryId?.toString() === excludeVehicleId.toString() || vehicle.id?.toString() === excludeVehicleId.toString())) {
        return false;
      }
      
      const normalizedExisting = (vehicle.vehicleNumber || '').trim().toLowerCase();
      return normalizedExisting === normalizedInput;
    });
  };

  const handleSaveClick = () => {
    if (!validateForm()) {
      return;
    }
    
    // Check for duplicate vehicle number
    if (checkDuplicateVehicleNumber(formData.vehicleNumber)) {
      setShowDuplicateErrorModal(true);
      return;
    }
    
    setSaveConfirmType('add');
    setShowSaveConfirm(true);
  };

  const handleUpdateClick = () => {
    if (!validateForm()) {
      return;
    }
    
    // Check for duplicate vehicle number (exclude current vehicle being edited)
    const currentVehicleId = editingVehicle?.primaryId || editingVehicle?.id;
    if (checkDuplicateVehicleNumber(formData.vehicleNumber, currentVehicleId)) {
      setShowDuplicateErrorModal(true);
      return;
    }
    
    setSaveConfirmType('update');
    setShowSaveConfirm(true);
  };

  const handleSaveConfirm = async () => {
    try {
      if (saveConfirmType === 'add') {
        // Prepare vehicle data for API
        const vehicleData = mapVehicleToAPI({
          vehicleNumber: formData.vehicleNumber,
          vehicleType: formData.vehicleType,
          capacity: parseInt(formData.capacity) || 0,
          agency: formData.agency,
          agencyId: formData.agencyId || null,
          status: formData.status,
          assignedDriver: formData.assignedDriver || 'Not Assigned',
          assignedDriverId: formData.assignedDriverId || null,
          photo: formData.photo || null, // Send the actual File object, not the preview
          documents: formData.documents || {}
        });

        // Create vehicle via API
        const response = await createVehicle(vehicleData);
        // API response structure: { success, message, data }
        const createdVehicle = response.data || response;
        
        setSuccessVehicleId(createdVehicle?.vehicle_id || createdVehicle?.id || '');
        setSuccessVehicleNumber(formData.vehicleNumber);
        setSuccessModalType('add');
        resetForm();
        setShowAddModal(false);
        setShowSaveConfirm(false);
        setShowSuccessModal(true);
        
        // Refresh vehicle list
        await fetchVehicles();
      } else if (saveConfirmType === 'update') {
        // Prepare vehicle data for API
        // For photo: use formData.photo (File object) if a new photo was selected,
        // use null if photo was removed, or undefined if photo not changed
        let photoToSend = undefined;
        if (formData.photo instanceof File) {
          // New photo file was selected - send the File object
          photoToSend = formData.photo;
        } else if (formData.photo === null && formData.photoPreview === null && editingVehicle.photo) {
          // Photo was explicitly removed (both null and vehicle had a photo before)
          photoToSend = null;
        }
        // If photoToSend is undefined, mapVehicleToAPI won't include the photo field
        
        const vehicleData = mapVehicleToAPI({
          vehicleNumber: formData.vehicleNumber,
          vehicleType: formData.vehicleType,
          capacity: parseInt(formData.capacity) || 0,
          agency: formData.agency,
          agencyId: formData.agencyId || editingVehicle.agencyId,
          status: formData.status,
          assignedDriver: formData.assignedDriver || 'Not Assigned',
          assignedDriverId: formData.assignedDriverId || editingVehicle.assignedDriverId,
          photo: photoToSend,
          documents: formData.documents || {}
        });

        // Update vehicle via API
        // Prefer the API primary ID (primaryId) when available; fall back to editingVehicle.id
        const updateId = editingVehicle.primaryId ?? editingVehicle.id;
        await updateVehicle(updateId, vehicleData);
        
        setSuccessVehicleNumber(formData.vehicleNumber);
        setSuccessModalType('update');
        resetForm();
        setShowEditModal(false);
        setShowSaveConfirm(false);
        setShowSuccessModal(true);
        
        // Refresh vehicle list
        await fetchVehicles();
      }
    } catch (err) {
      console.error('Error saving vehicle:', err);
      const errorMessage = err.message || 'Failed to save vehicle. Please try again.';
      
      // Check if error is related to duplicate vehicle number
      const isDuplicateError = errorMessage.toLowerCase().includes('duplicate') || 
                                errorMessage.toLowerCase().includes('already exists') ||
                                errorMessage.toLowerCase().includes('vehicle number');
      
      if (isDuplicateError) {
        setShowDuplicateErrorModal(true);
      } else {
        setError(errorMessage);
      }
      setShowSaveConfirm(false);
    }
  };

  const handleSaveCancel = () => {
    setShowSaveConfirm(false);
    setSaveConfirmType(null);
  };


  // Get all agencies from API for filter dropdown (not just those with vehicles)
  // This ensures all transporters are listed, even if they don't have vehicles yet
  const uniqueAgencies = agenciesList.length > 0 
    ? agenciesList.map(a => a.name).filter(Boolean).sort()
    : [...new Set(vehicles.map(v => v.agency).filter(Boolean))].sort();

  // Calculate document expiry from actual documents
  const calculateDocumentExpiry = (vehicle) => {
    // If vehicle has documents with dates, calculate from them
    if (vehicle.documents) {
      const documentTypes = ['rcBook', 'insurance', 'pollutionCertificate', 'fitnessCertificate', 'permit', 'roadTax'];
      const total = documentTypes.length;
      let valid = 0;
      let expired = 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      documentTypes.forEach(docType => {
        const doc = vehicle.documents[docType];
        if (doc && doc.date) {
          const expiryDate = new Date(doc.date);
          expiryDate.setHours(0, 0, 0, 0);
          
          if (expiryDate >= today) {
            valid++;
          } else {
            expired++;
          }
        }
      });

      return { total, valid, expired };
    }
    
    // Fallback to documentsExpiry property if documents not available (for initial mock data)
    if (vehicle.documentsExpiry) {
      return vehicle.documentsExpiry;
    }
    
    // Default if neither exists
    return { total: 7, valid: 0, expired: 0 };
  };

  // Get document validity status
  const getDocumentValidityStatus = (doc) => {
    if (doc.expired > 0) return 'Expired';
    if (doc.valid === doc.total && doc.total > 0) return 'Valid';
    if (doc.valid > 0 && doc.valid < doc.total && doc.expired === 0) return 'Expire Soon';
    return 'Valid'; // Default
  };

  // Note: Search, status, and agency filters are now handled server-side via API
  // Only document validity filter is handled client-side since it's calculated
  const filteredVehicles = vehicles.filter(vehicle => {
    // Document validity filter (client-side only)
    if (documentValidityFilter !== '') {
      const docExpiry = calculateDocumentExpiry(vehicle);
      const docStatus = getDocumentValidityStatus(docExpiry);
      return docStatus === documentValidityFilter;
    }
    return true;
  });

  // Reset to page 1 when filters change (except documentValidityFilter which is client-side)
  useEffect(() => {
    if (documentValidityFilter === '') {
      setCurrentPage(1);
    }
  }, [statusFilter, agencyFilter, itemsPerPage]);
  
  // Reset to page 1 when document validity filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [documentValidityFilter]);

  // Auto-hide success modals
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  useEffect(() => {
    if (showDeleteSuccess) {
      const timer = setTimeout(() => {
        setShowDeleteSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showDeleteSuccess]);

  useEffect(() => {
    if (showDocumentSuccess) {
      const timer = setTimeout(() => {
        setShowDocumentSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showDocumentSuccess]);

  useEffect(() => {
    if (showImportSuccess) {
      const timer = setTimeout(() => {
        setShowImportSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showImportSuccess]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
      if (agencyDropdownRef.current && !agencyDropdownRef.current.contains(event.target)) {
        setAgencyDropdownOpen(false);
      }
      if (documentDropdownRef.current && !documentDropdownRef.current.contains(event.target)) {
        setDocumentDropdownOpen(false);
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStatusBadgeClass = (status) => {
    return status === 'Active' ? 'status-badge active' : 'status-badge inactive';
  };

  const getDocumentProgressClass = (doc) => {
    const status = getDocumentValidityStatus(doc);
    if (status === 'Expired') return 'doc-progress expired';
    if (status === 'Valid') return 'doc-progress valid';
    return 'doc-progress neutral'; // Expire Soon
  };

  const getProgressPercentage = (doc) => {
    if (doc.total === 0) return 0;
    return (doc.valid / doc.total) * 100;
  };

  // Parse CSV text into array of objects using headers (handles quotes and commas)
  const parseCSV = (text) => {
    const rows = [];
    let current = '';
    let inQuotes = false;
    let row = [];
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const next = text[i + 1];
      if (char === '"') {
        if (inQuotes && next === '"') { // Escaped quote
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        row.push(current);
        current = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        if (current !== '' || row.length > 0) {
          row.push(current);
          rows.push(row);
          row = [];
          current = '';
        }
      } else {
        current += char;
      }
    }
    if (current !== '' || row.length > 0) {
      row.push(current);
      rows.push(row);
    }
    // Remove possible empty last line
    if (rows.length && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0].trim() === '') {
      rows.pop();
    }
    if (rows.length === 0) return [];
    const headers = rows[0].map(h => h.trim());
    return rows.slice(1).map(r => {
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = (r[idx] ?? '').trim();
      });
      return obj;
    });
  };

  // Map CSV row to our vehicle object
  const mapRowToVehicle = (row) => {
    const get = (key) => row[key] ?? '';
    const id = get('Vehicle ID') || ('VHC' + String(Math.floor(Math.random() * 10000)).padStart(4, '0'));
    const capacityVal = parseInt(get('Capacity (kg)').replace(/[,\s]/g, ''), 10);
    const documents = {
      rcBook: { documentNumber: get('RC Book Number') || '', date: get('RC Book Expiry Date') || '' },
      insurance: { documentNumber: get('Insurance Number') || '', date: get('Insurance Expiry Date') || '' },
      pollutionCertificate: { documentNumber: get('Pollution Certificate Number') || '', date: get('Pollution Certificate Expiry Date') || '' },
      fitnessCertificate: { documentNumber: get('Fitness Certificate Number') || '', date: get('Fitness Certificate Expiry Date') || '' },
      permit: { documentNumber: get('Permit Number') || '', date: get('Permit Expiry Date') || '' },
      roadTax: { documentNumber: get('Road Tax Number') || '', date: get('Road Tax Expiry Date') || '' }
    };
    // Normalize docs shape to match internal state
    Object.keys(documents).forEach(k => {
      documents[k] = {
        fileName: null,
        preview: null,
        date: documents[k].date,
        documentNumber: documents[k].documentNumber,
        file: null
      };
    });
    return {
      id,
      vehicleNumber: get('Vehicle Number'),
      vehicleType: get('Vehicle Type'),
      capacity: isNaN(capacityVal) ? 0 : capacityVal,
      agency: get('Transporter') || get('Agency'), // Support both "Transporter" (Excel header) and "Agency" (legacy CSV)
      status: get('Status') || 'Active',
      assignedDriver: get('Assigned Driver') || 'Not Assigned',
      photo: null,
      documents
    };
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      let rows = [];

      // Check if file is Excel (.xlsx) or CSV
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        // Read Excel file
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        
        // Convert to same format as CSV parser
        rows = jsonData.map(row => {
          const obj = {};
          Object.keys(row).forEach(key => {
            // Remove asterisk from header names if present
            const cleanKey = key.replace(/\s*\*\s*$/, '').trim();
            obj[cleanKey] = String(row[key] || '').trim();
          });
          return obj;
        });
      } else {
        // Read CSV file
        const text = await file.text();
        rows = parseCSV(text);
      }

      // Filter out empty rows (missing mandatory fields like Vehicle Number)
      const nonEmptyRows = rows.filter(r => (r['Vehicle Number'] || '').trim() !== '');

      // Validation using vehicle number
      const existingNumbers = new Set(vehicles.map(v => v.vehicleNumber.toLowerCase()));
      const seenInFile = new Set();
      const valid = [];
      const invalid = [];

      nonEmptyRows.forEach((r, idx) => {
        const vn = (r['Vehicle Number'] || '').trim();
        const vnLower = vn.toLowerCase();
        if (!vn) {
          invalid.push({ row: idx + 2, vehicleNumber: vn, reason: 'Vehicle Number missing' });
        } else if (existingNumbers.has(vnLower)) {
          invalid.push({ row: idx + 2, vehicleNumber: vn, reason: 'Duplicate of existing record' });
        } else if (seenInFile.has(vnLower)) {
          invalid.push({ row: idx + 2, vehicleNumber: vn, reason: 'Duplicate in file' });
        } else {
          seenInFile.add(vnLower);
          valid.push(mapRowToVehicle(r));
        }
      });

      setImportFileName(file.name);
      setImportValidVehicles(valid);
      setImportInvalidRows(invalid);
      setShowImportModal(true);
    } catch (err) {
      console.error('Import error:', err);
      alert('Failed to import file. Please ensure it matches the sample format. Error: ' + (err.message || 'Unknown error'));
    } finally {
      e.target.value = '';
    }
  };

  const handleImportConfirm = async () => {
    if (importValidVehicles.length === 0) return;

    try {
      setLoading(true);
      setError('');
      
      let successCount = 0;
      let failedCount = 0;
      const failedVehicles = [];

      // Process each vehicle
      for (const vehicle of importValidVehicles) {
        try {
          // Find agency ID from agency name (Transporter field)
          let agencyId = vehicle.agencyId;
          if (!agencyId && vehicle.agency) {
            const agencyNameToFind = vehicle.agency.trim();
            
            // Debug: Log what we're looking for
            console.log(`Looking for agency: "${agencyNameToFind}"`);
            console.log(`Available agencies:`, agenciesList.map(a => ({ id: a.id, name: a.name || a.agency_name })));
            
            // Try exact match first (case-insensitive)
            let matchedAgency = agenciesList.find(
              a => {
                const agencyName = (a.name || a.agency_name || '').trim();
                return agencyName.toLowerCase() === agencyNameToFind.toLowerCase();
              }
            );
            
            // If not found, try partial match (in case of extra spaces or slight variations)
            if (!matchedAgency) {
              matchedAgency = agenciesList.find(
                a => {
                  const agencyName = (a.name || a.agency_name || '').trim().toLowerCase();
                  const searchName = agencyNameToFind.toLowerCase();
                  return agencyName === searchName || 
                         agencyName.includes(searchName) || 
                         searchName.includes(agencyName);
                }
              );
            }
            
            if (matchedAgency && matchedAgency.id) {
              agencyId = matchedAgency.id;
              console.log(`Found agency: "${matchedAgency.name || matchedAgency.agency_name}" with ID: ${agencyId}`);
            } else {
              // Log error with details
              console.error(`Agency "${agencyNameToFind}" not found in agencies list. Available agencies:`, 
                agenciesList.map(a => a.name || a.agency_name));
              throw new Error(`Transporter "${agencyNameToFind}" not found. Please ensure the transporter name matches exactly.`);
            }
          }
          
          // Ensure agencyId is an integer or null
          if (agencyId !== null && agencyId !== undefined) {
            agencyId = parseInt(agencyId, 10);
            if (isNaN(agencyId)) {
              console.error(`Invalid agencyId: ${vehicle.agencyId}, setting to null`);
              agencyId = null;
            }
          } else {
            agencyId = null;
          }
          
          // Validate that we have an agencyId for required field
          if (!agencyId && vehicle.agency) {
            throw new Error(`Could not resolve Transporter "${vehicle.agency}" to an agency ID`);
          }

          // Find driver ID from driver name (optional)
          let assignedDriverId = vehicle.assignedDriverId;
          if (!assignedDriverId && vehicle.assignedDriver && vehicle.assignedDriver !== 'Not Assigned') {
            // Extract driver name (remove mobile number if present)
            const driverName = vehicle.assignedDriver.split(' (')[0].trim();
            const matchedDriver = drivers.find(
              d => (d.name || d.full_name || '').toLowerCase() === driverName.toLowerCase()
            );
            if (matchedDriver) {
              assignedDriverId = matchedDriver.id;
            }
            // If driver not found, continue without assigned driver (it's optional)
          }

          // Prepare vehicle data for API
          const vehicleData = mapVehicleToAPI({
            vehicleNumber: vehicle.vehicleNumber,
            vehicleType: vehicle.vehicleType,
            capacity: vehicle.capacity || 0,
            agency: vehicle.agency,
            agencyId: agencyId,
            status: vehicle.status || 'Active',
            assignedDriver: vehicle.assignedDriver || 'Not Assigned',
            assignedDriverId: assignedDriverId || null,
            photo: vehicle.photo || null,
            documents: vehicle.documents || {}
          });

          // Create vehicle via API
          const createResponse = await createVehicle(vehicleData);
          const createdVehicle = createResponse?.data || createResponse;
          const vehicleId = createdVehicle?.id || createdVehicle?.vehicle_id;

          // Save documents to vehicle_documents table if vehicle was created and documents exist
          if (vehicleId && vehicle.documents) {
            try {
              const documentsToSave = [];
              
              // Convert documents from import format to API format
              Object.entries(vehicle.documents).forEach(([docKey, docData]) => {
                if (docData && (docData.documentNumber || docData.date)) {
                  const documentType = getDocumentLabel(docKey);
                  const documentNumber = docData.documentNumber?.trim();
                  const expiryDate = docData.date?.trim();
                  
                  // Only add if at least document number or expiry date is provided
                  if (documentNumber || expiryDate) {
                    // Convert date format if needed (Excel might have different formats)
                    let formattedDate = expiryDate;
                    if (expiryDate) {
                      // Try to parse and format the date
                      const dateObj = new Date(expiryDate);
                      if (!isNaN(dateObj.getTime())) {
                        // Format as YYYY-MM-DD
                        formattedDate = formatDateForInput(dateObj.toISOString());
                      } else {
                        // Try common date formats
                        const formats = [
                          /(\d{2})\/(\d{2})\/(\d{4})/, // DD/MM/YYYY
                          /(\d{4})-(\d{2})-(\d{2})/,   // YYYY-MM-DD
                          /(\d{2})-(\d{2})-(\d{4})/    // DD-MM-YYYY
                        ];
                        for (const format of formats) {
                          const match = expiryDate.match(format);
                          if (match) {
                            if (format === formats[0]) {
                              // DD/MM/YYYY
                              formattedDate = `${match[3]}-${match[2]}-${match[1]}`;
                            } else if (format === formats[1]) {
                              // YYYY-MM-DD
                              formattedDate = expiryDate;
                            } else {
                              // DD-MM-YYYY
                              formattedDate = `${match[3]}-${match[2]}-${match[1]}`;
                            }
                            break;
                          }
                        }
                      }
                    }
                    
                    documentsToSave.push({
                      document_type: documentType,
                      document_number: documentNumber || null,
                      expiry_date: formattedDate || null
                    });
                  }
                }
              });

              // Save documents if any were provided
              if (documentsToSave.length > 0) {
                await saveVehicleDocuments(vehicleId, documentsToSave);
                console.log(`Saved ${documentsToSave.length} document(s) for vehicle ${vehicle.vehicleNumber}`);
              }
            } catch (docError) {
              // Log error but don't fail the import - documents can be added later via modal
              console.warn(`Failed to save documents for vehicle ${vehicle.vehicleNumber}:`, docError);
            }
          }

          successCount++;
        } catch (err) {
          console.error(`Failed to create vehicle ${vehicle.vehicleNumber}:`, err);
          failedCount++;
          failedVehicles.push({
            vehicleNumber: vehicle.vehicleNumber,
            error: err.message || 'Unknown error'
          });
        }
      }

      // Update state
      setImportSuccessCount(successCount);
      setImportSkippedCount(importInvalidRows.length + failedCount);
      setShowImportModal(false);
      setShowImportSuccess(true);
      setImportValidVehicles([]);
      setImportInvalidRows([]);
      setImportFileName('');

      // Refresh vehicle list
      await fetchVehicles();

      // Show error message if some vehicles failed
      if (failedCount > 0) {
        const errorMsg = `${failedCount} vehicle(s) failed to import:\n${failedVehicles.map(v => `- ${v.vehicleNumber}: ${v.error}`).join('\n')}`;
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Error during import:', err);
      setError(err.message || 'Failed to import vehicles. Please try again.');
      alert(`Import failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImportCancel = () => {
    setShowImportModal(false);
    setImportValidVehicles([]);
    setImportInvalidRows([]);
    setImportFileName('');
  };

  // Download sample Excel template with formatting
  const handleDownloadSampleCSV = async () => {
    // Define required fields (will have asterisk)
    const requiredFields = ['Vehicle Number', 'Vehicle Type', 'Capacity (kg)', 'Transporter', 'Status'];
    
    const headers = [
      'Vehicle Number',
      'Vehicle Type',
      'Capacity (kg)',
      'Transporter',
      'Status',
      'Assigned Driver',
      'RC Book Number',
      'Insurance Number',
      'Pollution Certificate Number',
      'Fitness Certificate Number',
      'Permit Number',
      'Road Tax Number',
      'RC Book Expiry Date',
      'Insurance Expiry Date',
      'Pollution Certificate Expiry Date',
      'Fitness Certificate Expiry Date',
      'Permit Expiry Date',
      'Road Tax Expiry Date'
    ];

    // Add asterisk to required fields
    const headersWithAsterisks = headers.map(header => 
      requiredFields.includes(header) ? `${header} *` : header
    );

    // Create workbook and worksheet using ExcelJS
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Vehicle Import Template');

    // Add header row
    worksheet.addRow(headersWithAsterisks);

    // Add sample data row with example dates
    const sampleDate = new Date();
    sampleDate.setFullYear(sampleDate.getFullYear() + 1); // One year from now
    const sampleDateStr = sampleDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Format as DD/MM/YYYY for display (more common in India)
    const day = String(sampleDate.getDate()).padStart(2, '0');
    const month = String(sampleDate.getMonth() + 1).padStart(2, '0');
    const year = sampleDate.getFullYear();
    const sampleDateFormatted = `${day}/${month}/${year}`; // DD/MM/YYYY
    
    const sampleRow = [
      'TN70AV6512',                    // Vehicle Number
      'Container',                      // Vehicle Type
      '1000',                          // Capacity (kg)
      agenciesList.length > 0 ? (agenciesList[0].name || agenciesList[0].agency_name || '') : 'Agency Name', // Transporter
      'Active',                        // Status
      drivers.length > 0 ? (drivers[0].name || drivers[0].full_name || '') : '', // Assigned Driver
      'RC123456',                      // RC Book Number
      'INS789012',                     // Insurance Number
      'PUC345678',                     // Pollution Certificate Number
      'FIT901234',                     // Fitness Certificate Number
      'PER567890',                     // Permit Number
      'TAX123456',                     // Road Tax Number
      sampleDateFormatted,             // RC Book Expiry Date (DD/MM/YYYY)
      sampleDateFormatted,             // Insurance Expiry Date
      sampleDateFormatted,             // Pollution Certificate Expiry Date
      sampleDateFormatted,             // Fitness Certificate Expiry Date
      sampleDateFormatted,             // Permit Expiry Date
      sampleDateFormatted              // Road Tax Expiry Date
    ];
    worksheet.addRow(sampleRow);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Style sample row (row 2) - make it light gray to indicate it's an example
    const sampleDataRow = worksheet.getRow(2);
    sampleDataRow.font = { italic: true, color: { argb: 'FF666666' } };
    sampleDataRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF5F5F5' }
    };

    // Set column widths for better visibility
    worksheet.getColumn(1).width = 22; // Vehicle Number *
    worksheet.getColumn(2).width = 18; // Vehicle Type *
    worksheet.getColumn(3).width = 17; // Capacity (kg) *
    worksheet.getColumn(4).width = 25; // Transporter *
    worksheet.getColumn(5).width = 15; // Status *
    worksheet.getColumn(6).width = 25; // Assigned Driver
    worksheet.getColumn(7).width = 20; // RC Book Number
    worksheet.getColumn(8).width = 20; // Insurance Number
    worksheet.getColumn(9).width = 32; // Pollution Certificate Number
    worksheet.getColumn(10).width = 32; // Fitness Certificate Number
    worksheet.getColumn(11).width = 17; // Permit Number
    worksheet.getColumn(12).width = 20; // Road Tax Number
    worksheet.getColumn(13).width = 22; // RC Book Expiry Date
    worksheet.getColumn(14).width = 24; // Insurance Expiry Date
    worksheet.getColumn(15).width = 39; // Pollution Certificate Expiry Date
    worksheet.getColumn(16).width = 36; // Fitness Certificate Expiry Date
    worksheet.getColumn(17).width = 20; // Permit Expiry Date
    worksheet.getColumn(18).width = 22; // Road Tax Expiry Date

    // Add data validation (dropdowns) for dropdown fields
    // Note: Validations start from row 2 to include the sample row so it can be edited
    const vehicleTypeOptions = ['Lorry', 'Container', 'Van', 'Truck', 'Trailer'];
    const statusOptions = ['Active', 'Inactive'];

    // Assigned Driver dropdown (Column F, index 6) - use drivers list
    if (drivers && drivers.length > 0) {
      const driverOptions = drivers
        .filter(d => d.name)
        .map(driver => {
          const name = driver.name || driver.full_name || '';
          const mobile = driver.mobile_number ? ` (${driver.mobile_number})` : '';
          return `${name}${mobile}`;
        })
        .filter(Boolean);
      
      if (driverOptions.length > 0) {
        // Excel has a limit of 255 characters for list data validation
        // If too long, we'll use a helper sheet approach or truncate
        const driverListStr = driverOptions.join(',');
        if (driverListStr.length <= 255) {
          for (let row = 2; row <= 1000; row++) { // Include row 2 (sample row)
            worksheet.getCell(row, 6).dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: [`"${driverListStr}"`],
              showInputMessage: true,
              promptTitle: 'Assigned Driver',
              prompt: 'Please select a driver from the dropdown (optional)'
            };
          }
        }
      }
    }

    // Add date format validation and input messages for expiry date columns
    // Date columns: 13-18 (RC Book, Insurance, Pollution, Fitness, Permit, Road Tax)
    const dateColumns = [13, 14, 15, 16, 17, 18];
    const dateColumnNames = [
      'RC Book Expiry Date',
      'Insurance Expiry Date',
      'Pollution Certificate Expiry Date',
      'Fitness Certificate Expiry Date',
      'Permit Expiry Date',
      'Road Tax Expiry Date'
    ];

    dateColumns.forEach((colIndex, idx) => {
      // Format date columns
      worksheet.getColumn(colIndex).numFmt = 'dd/mm/yyyy';
      
      // Add input message for date format guidance (include row 2 so sample row can be edited)
      for (let row = 2; row <= 1000; row++) {
        worksheet.getCell(row, colIndex).dataValidation = {
          type: 'custom',
          allowBlank: true,
          formula1: 'TRUE', // Allow any input, we'll validate on import
          showInputMessage: true,
          promptTitle: dateColumnNames[idx],
          prompt: 'Enter date in DD/MM/YYYY format (e.g., 31/12/2025). You can also use YYYY-MM-DD or DD-MM-YYYY.'
        };
      }
    });

    // Apply dropdown validations starting from row 2 (include sample row so it can be edited)
    // Vehicle Type dropdown (Column B, index 2)
    for (let row = 2; row <= 1000; row++) {
      worksheet.getCell(row, 2).dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [`"${vehicleTypeOptions.join(',')}"`],
        showInputMessage: true,
        promptTitle: 'Vehicle Type',
        prompt: 'Please select a vehicle type from the dropdown'
      };
    }

    // Status dropdown (Column E, index 5)
    for (let row = 2; row <= 1000; row++) {
      worksheet.getCell(row, 5).dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [`"${statusOptions.join(',')}"`],
        showInputMessage: true,
        promptTitle: 'Status',
        prompt: 'Please select a status from the dropdown'
      };
    }

    // Transporter dropdown (Column D, index 4) - use agenciesList
    if (agenciesList && agenciesList.length > 0) {
      const transporterOptions = agenciesList.map(agency => agency.name || agency.agency_name || '').filter(Boolean);
      if (transporterOptions.length > 0) {
        for (let row = 2; row <= 1000; row++) { // Include row 2 (sample row)
          worksheet.getCell(row, 4).dataValidation = {
            type: 'list',
            allowBlank: false,
            formulae: [`"${transporterOptions.join(',')}"`],
            showInputMessage: true,
            promptTitle: 'Transporter',
            prompt: 'Please select a transporter from the dropdown'
          };
        }
      }
    }

    // Generate Excel file and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Vehicle_Import_Sample.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCSVImportClick = () => {
    setShowSampleCSVModal(true);
  };

  const handleProceedWithImport = () => {
    setShowSampleCSVModal(false);
    document.getElementById('import-file-input').click();
  };

  // Helper function to parse date and return Date object for Excel
  const parseDateForExcel = (dateString) => {
    if (!dateString) return null;
    try {
      // Handle ISO date strings (e.g., "2027-11-30T18:30:00.000Z")
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null; // Return null if invalid
      return date;
    } catch (error) {
      // If already in YYYY-MM-DD format, parse it
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
        const dateOnly = dateString.split('T')[0]; // Remove time part if present
        const date = new Date(dateOnly);
        return isNaN(date.getTime()) ? null : date;
      }
      return null;
    }
  };

  // Export vehicles to Excel with proper column widths
  const handleExport = async () => {
    const headers = [
      'Vehicle ID',
      'Vehicle Number',
      'Vehicle Type',
      'Capacity (kg)',
      'Transporter',
      'Status',
      'Assigned Driver',
      'RC Book Number',
      'Insurance Number',
      'Pollution Certificate Number',
      'Fitness Certificate Number',
      'Permit Number',
      'Road Tax Number',
      'RC Book Expiry Date',
      'Insurance Expiry Date',
      'Pollution Certificate Expiry Date',
      'Fitness Certificate Expiry Date',
      'Permit Expiry Date',
      'Road Tax Expiry Date'
    ];

    // Create workbook and worksheet using ExcelJS
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Vehicles Export');

    // Add header row
    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Add data rows
    vehicles.forEach((vehicle) => {
      const d = vehicle.documents || {};
      const row = [
        vehicle.id || '',
        vehicle.vehicleNumber || '',
        vehicle.vehicleType || '',
        vehicle.capacity || '',
        vehicle.agency || '',
        vehicle.status || '',
        vehicle.assignedDriver || 'Not Assigned',
        d.rcBook?.documentNumber || '',
        d.insurance?.documentNumber || '',
        d.pollutionCertificate?.documentNumber || '',
        d.fitnessCertificate?.documentNumber || '',
        d.permit?.documentNumber || '',
        d.roadTax?.documentNumber || '',
        parseDateForExcel(d.rcBook?.date || ''),
        parseDateForExcel(d.insurance?.date || ''),
        parseDateForExcel(d.pollutionCertificate?.date || ''),
        parseDateForExcel(d.fitnessCertificate?.date || ''),
        parseDateForExcel(d.permit?.date || ''),
        parseDateForExcel(d.roadTax?.date || '')
      ];
      worksheet.addRow(row);
    });

    // Set column widths to ensure headers are fully visible
    worksheet.getColumn(1).width = 12;  // Vehicle ID
    worksheet.getColumn(2).width = 18;  // Vehicle Number
    worksheet.getColumn(3).width = 18;  // Vehicle Type
    worksheet.getColumn(4).width = 17;  // Capacity (kg)
    worksheet.getColumn(5).width = 25;  // Transporter
    worksheet.getColumn(6).width = 15;  // Status
    worksheet.getColumn(7).width = 25;  // Assigned Driver
    worksheet.getColumn(8).width = 20;  // RC Book Number
    worksheet.getColumn(9).width = 20;  // Insurance Number
    worksheet.getColumn(10).width = 35; // Pollution Certificate Number
    worksheet.getColumn(11).width = 35; // Fitness Certificate Number
    worksheet.getColumn(12).width = 17; // Permit Number
    worksheet.getColumn(13).width = 20; // Road Tax Number
    worksheet.getColumn(14).width = 22; // RC Book Expiry Date
    worksheet.getColumn(15).width = 24; // Insurance Expiry Date
    worksheet.getColumn(16).width = 40; // Pollution Certificate Expiry Date
    worksheet.getColumn(17).width = 38; // Fitness Certificate Expiry Date
    worksheet.getColumn(18).width = 20; // Permit Expiry Date
    worksheet.getColumn(19).width = 22; // Road Tax Expiry Date
    
    // Set date format for date columns (columns 14-19)
    [14, 15, 16, 17, 18, 19].forEach(colIndex => {
      worksheet.getColumn(colIndex).numFmt = 'yyyy-mm-dd';
    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Vehicles_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Calculate pagination based on server-side total count
  // For document validity filter, use filtered count; otherwise use server total
  const displayTotal = documentValidityFilter !== '' ? filteredVehicles.length : totalCount;
  const totalPages = Math.ceil(displayTotal / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentVehicles = filteredVehicles;

  return (
    <div className="vehicle-management-container">
      {/* Header Section */}
      <div className="vehicle-header">
        <div className="filter-group">
          <div className="filter-wrapper" ref={statusDropdownRef}>
            <button 
              className={`filter-btn ${statusFilter ? 'active' : ''}`}
              onClick={() => {
                setStatusDropdownOpen(!statusDropdownOpen);
                setAgencyDropdownOpen(false);
                setDocumentDropdownOpen(false);
              }}
            >
              <span>▼</span> Filter by Status {statusFilter && `(${statusFilter})`}
            </button>
            {statusDropdownOpen && (
              <div className="filter-dropdown">
                <button
                  className={`filter-option ${statusFilter === '' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('');
                    setStatusDropdownOpen(false);
                  }}
                >
                  All
                </button>
                <button
                  className={`filter-option ${statusFilter === 'Active' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Active');
                    setStatusDropdownOpen(false);
                  }}
                >
                  Active
                </button>
                <button
                  className={`filter-option ${statusFilter === 'Inactive' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Inactive');
                    setStatusDropdownOpen(false);
                  }}
                >
                  Inactive
                </button>
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={agencyDropdownRef}>
            <button 
              className={`filter-btn ${agencyFilter ? 'active' : ''}`}
              onClick={() => {
                setAgencyDropdownOpen(!agencyDropdownOpen);
                setStatusDropdownOpen(false);
                setDocumentDropdownOpen(false);
              }}
            >
              <span>▼</span> Filter by Transporter {agencyFilter && `(${agencyFilter})`}
            </button>
            {agencyDropdownOpen && (
              <div className="filter-dropdown">
                <button
                  className={`filter-option ${agencyFilter === '' ? 'selected' : ''}`}
                  onClick={() => {
                    setAgencyFilter('');
                    setAgencyDropdownOpen(false);
                  }}
                >
                  All
                </button>
                {uniqueAgencies.map((agency) => (
                  <button
                    key={agency}
                    className={`filter-option ${agencyFilter === agency ? 'selected' : ''}`}
                    onClick={() => {
                      setAgencyFilter(agency);
                      setAgencyDropdownOpen(false);
                    }}
                  >
                    {agency}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={documentDropdownRef}>
            <button 
              className={`filter-btn ${documentValidityFilter ? 'active' : ''}`}
              onClick={() => {
                setDocumentDropdownOpen(!documentDropdownOpen);
                setStatusDropdownOpen(false);
                setAgencyDropdownOpen(false);
              }}
            >
              <span>▼</span> Document Validity {documentValidityFilter && `(${documentValidityFilter})`}
            </button>
            {documentDropdownOpen && (
              <div className="filter-dropdown">
                <button
                  className={`filter-option ${documentValidityFilter === '' ? 'selected' : ''}`}
                  onClick={() => {
                    setDocumentValidityFilter('');
                    setDocumentDropdownOpen(false);
                  }}
                >
                  All
                </button>
                <button
                  className={`filter-option ${documentValidityFilter === 'Valid' ? 'selected' : ''}`}
                  onClick={() => {
                    setDocumentValidityFilter('Valid');
                    setDocumentDropdownOpen(false);
                  }}
                >
                  Valid
                </button>
                <button
                  className={`filter-option ${documentValidityFilter === 'Expire Soon' ? 'selected' : ''}`}
                  onClick={() => {
                    setDocumentValidityFilter('Expire Soon');
                    setDocumentDropdownOpen(false);
                  }}
                >
                  Expire Soon
                </button>
                <button
                  className={`filter-option ${documentValidityFilter === 'Expired' ? 'selected' : ''}`}
                  onClick={() => {
                    setDocumentValidityFilter('Expired');
                    setDocumentDropdownOpen(false);
                  }}
                >
                  Expired
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="vehicle-actions">
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            <span>+</span> Add Vehicle
          </button>
          <button className="btn-icon-import" onClick={handleCSVImportClick} title="Import">
            <span>📥</span>
          </button>
          <input
            id="import-file-input"
            type="file"
                        accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleImportFileChange}
          />
          <button className="btn-icon-export" onClick={handleExport} title="Export">
            <span>📤</span>
          </button>
        </div>
      </div>

      {/* Show Entries and Search Section */}
      <div className="show-entries-container">
        <div className="show-entries-wrapper" style={{ width: '100%' }}>
          <div className="show-entries-left" ref={showEntriesDropdownRef}>
            <label className="show-entries-label">Show</label>
            <button
              className="show-entries-btn"
              onClick={() => {
                setShowEntriesDropdownOpen(!showEntriesDropdownOpen);
                setStatusDropdownOpen(false);
                setAgencyDropdownOpen(false);
                setDocumentDropdownOpen(false);
              }}
            >
              <span>{itemsPerPage}</span>
              <span>▼</span>
            </button>
            {showEntriesDropdownOpen && (
              <div className="show-entries-dropdown">
                {[10, 25, 50, 100, 500].map((num) => (
                  <button
                    key={num}
                    className={`show-entries-option ${itemsPerPage === num ? 'selected' : ''}`}
                    onClick={() => {
                      setItemsPerPage(num);
                      setShowEntriesDropdownOpen(false);
                      setCurrentPage(1);
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            )}
            <label className="show-entries-label">entries</label>
          </div>
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search vehicles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container" style={{ padding: '20px', textAlign: 'center' }}>
          <p>Loading vehicles...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="error-container" style={{ padding: '20px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px', margin: '10px 0' }}>
          <p>{error}</p>
          <button onClick={fetchVehicles} style={{ marginTop: '10px', padding: '8px 16px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      {/* Table Section */}
      {!loading && (
      <div className="vehicle-table-container">
        <table className="vehicle-table">
          <thead>
            <tr>
              <th>Vehicle ID</th>
              <th>Vehicle Number</th>
              <th>Vehicle Type</th>
              <th>Capacity</th>
              <th>Transporter</th>
              <th>Status</th>
              <th>Assigned Driver</th>
              <th>Documents Expiry</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentVehicles.map((vehicle) => (
              <tr key={vehicle.id}>
                <td>{vehicle.id}</td>
                <td>{vehicle.vehicleNumber}</td>
                <td>{vehicle.vehicleType}</td>
                <td>{vehicle.capacity.toLocaleString()} kg</td>
                <td>{vehicle.agency}</td>
                <td>
                  <span className={getStatusBadgeClass(vehicle.status)}>
                    {vehicle.status}
                  </span>
                </td>
                <td>{vehicle.assignedDriver}</td>
                <td>
                  {(() => {
                    const docExpiry = calculateDocumentExpiry(vehicle);
                    return (
                      <div 
                        className="doc-expiry-cell" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleImportClick(vehicle)}
                        title="Click to view documents"
                      >
                        <div className={getDocumentProgressClass(docExpiry)}>
                          <div
                            className="doc-progress-bar"
                            style={{ width: `${getProgressPercentage(docExpiry)}%` }}
                          ></div>
                        </div>
                        <div className="doc-expiry-info">
                          <span>{docExpiry.total} Total</span>
                          <span>{docExpiry.valid} Valid</span>
                          <span>{docExpiry.expired} Expired</span>
                        </div>
                      </div>
                    );
                  })()}
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit-btn" title="Edit" onClick={() => handleEditClick(vehicle)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.333 3.333L12.667 4.667M10.667 2L14 5.333L5.333 14H2V10.667L10.667 2Z" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    <button className="action-btn delete-btn" title="Delete" onClick={() => handleVehicleDeleteClick(vehicle)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="4" y="5.5" width="8" height="8.5" rx="0.5" stroke="#dc2626" strokeWidth="1.25" fill="none"/>
                        <path d="M2 5.5h12M6 5.5V4.25C6 3.83579 6.33579 3.5 6.75 3.5h2.5C9.66421 3.5 10 3.83579 10 4.25V5.5" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                        <line x1="6.5" y1="8.5" x2="6.5" y2="12" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                        <line x1="9.5" y1="8.5" x2="9.5" y2="12" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                      </svg>
                    </button>
                    <button className="action-btn import-btn" title="Import" onClick={() => handleImportClick(vehicle)}>
                      📥
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination Section */}
      {!loading && (
      <div className="vehicle-pagination">
        <div className="pagination-info">
          Showing {displayTotal > 0 ? startIndex + 1 : 0} to {Math.min(endIndex, displayTotal)} of {displayTotal} results
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            ←
          </button>
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index + 1}
              className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            →
          </button>
        </div>
      </div>
      )}

      {/* Add Vehicle Modal */}
      {showAddModal && (
        <div className="add-vehicle-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <h2 className="modal-title">Add New Vehicle</h2>
              <button className="modal-close-btn" onClick={() => setShowAddModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body-content">
              <div className="form-content">
                {/* Form Fields Column - 3 Column Grid */}
                <div className="form-fields-column">
                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📄</span> Vehicle Number <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input-field ${formErrors.vehicleNumber ? 'error' : ''}`}
                      placeholder="Enter vehicle number"
                      value={formData.vehicleNumber}
                      onChange={(e) => {
                        setFormData({...formData, vehicleNumber: e.target.value});
                        if (formErrors.vehicleNumber) {
                          setFormErrors({...formErrors, vehicleNumber: ''});
                        }
                      }}
                    />
                    {formErrors.vehicleNumber && <span className="error-message">{formErrors.vehicleNumber}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🚚</span> Vehicle Type <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${formErrors.vehicleType ? 'error' : ''}`}
                      value={formData.vehicleType}
                      onChange={(e) => {
                        setFormData({...formData, vehicleType: e.target.value});
                        if (formErrors.vehicleType) {
                          setFormErrors({...formErrors, vehicleType: ''});
                        }
                      }}
                    >
                      <option value="">Select vehicle type</option>
                      <option value="Lorry">Lorry</option>
                      <option value="Container">Container</option>
                      <option value="Van">Van</option>
                    </select>
                    {formErrors.vehicleType && <span className="error-message">{formErrors.vehicleType}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👥</span> Capacity <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-with-suffix">
                      <input
                        type="number"
                        className={`form-input-field ${formErrors.capacity ? 'error' : ''}`}
                        placeholder="Enter capacity"
                        value={formData.capacity}
                        onChange={(e) => {
                          setFormData({...formData, capacity: e.target.value});
                          if (formErrors.capacity) {
                            setFormErrors({...formErrors, capacity: ''});
                          }
                        }}
                      />
                      <span className="input-suffix">kg</span>
                    </div>
                    {formErrors.capacity && <span className="error-message">{formErrors.capacity}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🏢</span> Transporter <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${formErrors.agency ? 'error' : ''}`}
                      value={formData.agencyId || ''}
                      onChange={(e) => {
                        const selectedAgencyId = e.target.value ? parseInt(e.target.value) : null;
                        const selectedAgency = agenciesList.find(a => a.id === selectedAgencyId);
                        setFormData({
                          ...formData, 
                          agencyId: selectedAgencyId,
                          agency: selectedAgency ? selectedAgency.name : ''
                        });
                        if (formErrors.agency) {
                          setFormErrors({...formErrors, agency: ''});
                        }
                      }}
                    >
                      <option value="">Select Transporter</option>
                      {agenciesList.map((agency) => (
                        <option key={agency.id} value={agency.id}>
                          {agency.name}
                        </option>
                      ))}
                    </select>
                    {formErrors.agency && <span className="error-message">{formErrors.agency}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👤</span> Assigned Driver
                    </label>
                    <select
                      className="form-input-field"
                      value={formData.assignedDriverId || ''}
                      onChange={(e) => {
                        const selectedDriverId = e.target.value ? parseInt(e.target.value) : null;
                        const selectedDriver = drivers.find(d => d.id === selectedDriverId);
                        setFormData({
                          ...formData,
                          assignedDriverId: selectedDriverId,
                          assignedDriver: selectedDriver ? selectedDriver.name : ''
                        });
                      }}
                    >
                      <option value="">Select driver</option>
                      {filteredDrivers.map((driver) => (
                        <option key={driver.id} value={driver.id}>
                          {driver.name} {driver.mobile_number ? `(${driver.mobile_number})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🏢</span> Status <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${formErrors.status ? 'error' : ''}`}
                      value={formData.status}
                      onChange={(e) => {
                        setFormData({...formData, status: e.target.value});
                        if (formErrors.status) {
                          setFormErrors({...formErrors, status: ''});
                        }
                      }}
                    >
                      <option value="">Select Status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                    {formErrors.status && <span className="error-message">{formErrors.status}</span>}
                  </div>
                </div>

                {/* Right Column - Photo Upload */}
                <div className="photo-upload-column">
                  <div className="photo-upload-box">
                    {formData.photoPreview ? (
                      <>
                        <img src={formData.photoPreview} alt="Vehicle preview" className="photo-preview" />
                        <button
                          type="button"
                          className="photo-remove-btn"
                          onClick={() => {
                            setFormData({...formData, photo: null, photoPreview: null});
                            document.getElementById('add-photo-input').value = '';
                          }}
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="photo-upload-icon">📷</div>
                        <p className="photo-upload-text">Upload Photo</p>
                        <p className="photo-upload-hint">JPG or PNG (max 5MB)</p>
                      </>
                    )}
                    <input
                      type="file"
                      id="add-photo-input"
                      accept="image/jpeg,image/png"
                      className="photo-upload-input"
                      onChange={handlePhotoUpload}
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="modal-btn-save" onClick={handleSaveClick}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Vehicle Modal */}
      {showEditModal && editingVehicle && (
        <div className="add-vehicle-modal-overlay" onClick={() => {
          setShowEditModal(false);
          resetForm();
        }}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <h2 className="modal-title">Edit Vehicle</h2>
              <button className="modal-close-btn" onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}>
                ✕
              </button>
            </div>

            <div className="modal-body-content">
              <div className="form-content">
                {/* Left Column - Form Fields */}
                <div className="form-fields-column">
                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📄</span> Vehicle Number
                    </label>
                    <input
                      type="text"
                      className="form-input-field"
                      placeholder="Enter vehicle number"
                      value={formData.vehicleNumber}
                      onChange={(e) => setFormData({...formData, vehicleNumber: e.target.value})}
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🚚</span> Vehicle Type
                    </label>
                    <select
                      className="form-input-field"
                      value={formData.vehicleType}
                      onChange={(e) => setFormData({...formData, vehicleType: e.target.value})}
                    >
                      <option value="">Select vehicle type</option>
                      <option value="Lorry">Lorry</option>
                      <option value="Container">Container</option>
                      <option value="Van">Van</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👥</span> Capacity
                    </label>
                    <div className="input-with-suffix">
                      <input
                        type="number"
                        className="form-input-field"
                        placeholder="Enter capacity"
                        value={formData.capacity}
                        onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                      />
                      <span className="input-suffix">kg</span>
                    </div>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🏢</span> Transporter
                    </label>
                    <select
                      className="form-input-field"
                      value={formData.agencyId || ''}
                      onChange={(e) => {
                        const selectedAgencyId = e.target.value ? parseInt(e.target.value) : null;
                        const selectedAgency = agenciesList.find(a => a.id === selectedAgencyId);
                        setFormData({
                          ...formData, 
                          agencyId: selectedAgencyId,
                          agency: selectedAgency ? selectedAgency.name : ''
                        });
                      }}
                    >
                      <option value="">Select Transporter</option>
                      {agenciesList.map((agency) => (
                        <option key={agency.id} value={agency.id}>
                          {agency.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👤</span> Assigned Driver
                    </label>
                    <select
                      className="form-input-field"
                      value={formData.assignedDriverId || ''}
                      onChange={(e) => {
                        const selectedDriverId = e.target.value ? parseInt(e.target.value) : null;
                        const selectedDriver = drivers.find(d => d.id === selectedDriverId);
                        setFormData({
                          ...formData,
                          assignedDriverId: selectedDriverId,
                          assignedDriver: selectedDriver ? selectedDriver.name : ''
                        });
                      }}
                    >
                      <option value="">Select driver</option>
                      {drivers.map((driver) => (
                        <option key={driver.id} value={driver.id}>
                          {driver.name} {driver.mobile_number ? `(${driver.mobile_number})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🏢</span> Status <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${formErrors.status ? 'error' : ''}`}
                      value={formData.status}
                      onChange={(e) => {
                        setFormData({...formData, status: e.target.value});
                        if (formErrors.status) {
                          setFormErrors({...formErrors, status: ''});
                        }
                      }}
                    >
                      <option value="">Select Status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                    {formErrors.status && <span className="error-message">{formErrors.status}</span>}
                  </div>
                </div>

                {/* Right Column - Photo Upload */}
                <div className="photo-upload-column">
                  <div className="photo-upload-box">
                    {formData.photoPreview ? (
                      <>
                        <img src={formData.photoPreview} alt="Vehicle preview" className="photo-preview" />
                        <button
                          type="button"
                          className="photo-remove-btn"
                          onClick={() => handleDeleteClick('photo')}
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="photo-upload-icon">📷</div>
                        <p className="photo-upload-text">Upload Photo</p>
                        <p className="photo-upload-hint">JPG or PNG (max 5MB)</p>
                      </>
                    )}
                    <input
                      type="file"
                      id="edit-photo-input"
                      accept="image/jpeg,image/png"
                      className="photo-upload-input"
                      onChange={handlePhotoUpload}
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}>
                Cancel
              </button>
              <button className="modal-btn-save" onClick={handleUpdateClick}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this {deleteType === 'photo' ? 'photo' : 'document'}?</p>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel-button" onClick={handleDeleteCancel}>
                No
              </button>
              <button className="modal-button confirm-button" onClick={handleDeleteConfirm}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save/Update Confirmation Modal */}
      {showSaveConfirm && (
        <div className="modal-overlay" onClick={handleSaveCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm {saveConfirmType === 'add' ? 'Add' : 'Update'}</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to {saveConfirmType === 'add' ? 'add' : 'update'} this vehicle?</p>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel-button" onClick={handleSaveCancel}>
                No
              </button>
              <button className="modal-button confirm-button" onClick={handleSaveConfirm}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Vehicle Number Error Modal */}
      {showDuplicateErrorModal && (
        <div className="modal-overlay" onClick={() => setShowDuplicateErrorModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: '#DC2626' }}>Duplicate Vehicle Number</h3>
            </div>
            <div className="modal-body">
              <p style={{ color: '#DC2626', fontWeight: '500' }}>
                Vehicle number already exists. Please use a different vehicle number.
              </p>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-button confirm-button" 
                onClick={() => setShowDuplicateErrorModal(false)}
                style={{ width: '100%' }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Vehicle Confirmation Modal */}
      {showDeleteVehicleConfirm && vehicleToDelete && (
        <div className="modal-overlay" onClick={handleVehicleDeleteCancel}>
          <div className="delete-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-vehicle-header">
              <div className="delete-warning-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="delete-vehicle-title">Delete Vehicle</h3>
            </div>
            <div className="delete-vehicle-body">
              <p className="delete-vehicle-message">
                Are you sure you want to delete this vehicle? This action cannot be undone.
              </p>
              <div className="delete-vehicle-details">
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Vehicle ID:</span>
                  <span className="delete-detail-value">{vehicleToDelete.id}</span>
                </div>
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Vehicle Number:</span>
                  <span className="delete-detail-value">{vehicleToDelete.vehicleNumber}</span>
                </div>
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Vehicle Type:</span>
                  <span className="delete-detail-value">{vehicleToDelete.vehicleType}</span>
                </div>
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Owner:</span>
                  <span className="delete-detail-value">{vehicleToDelete.agency}</span>
                </div>
              </div>
            </div>
            <div className="delete-vehicle-footer">
              <button className="delete-vehicle-cancel-btn" onClick={handleVehicleDeleteCancel}>
                Cancel
              </button>
              <button className="delete-vehicle-delete-btn" onClick={handleVehicleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Modal */}
      {showDeleteSuccess && deletedVehicleInfo && (
        <div className="success-modal-overlay" onClick={() => setShowDeleteSuccess(false)}>
          <div className="delete-success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-success-header">
              <div className="delete-success-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="12" fill="#10B981"/>
                  <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="delete-success-title">Success</span>
              <button className="delete-success-close" onClick={() => setShowDeleteSuccess(false)}>
                ✕
              </button>
            </div>
            <div className="delete-success-body">
              <p className="delete-success-message">Vehicle successfully deleted</p>
              <div className="delete-success-details">
                <div className="delete-success-detail-row">
                  <span className="delete-success-detail-label">Vehicle ID:</span>
                  <span className="delete-success-detail-value">{deletedVehicleInfo.id}</span>
                </div>
                <div className="delete-success-detail-row">
                  <span className="delete-success-detail-label">Number:</span>
                  <span className="delete-success-detail-value">{deletedVehicleInfo.number}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Success Modal */}
      {showImportSuccess && (
        <div className="success-modal-overlay" onClick={() => setShowImportSuccess(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Import Successful</h2>
            <p className="success-message">{importSuccessCount} vehicle(s) imported. {importSkippedCount > 0 ? `${importSkippedCount} skipped.` : ''}</p>
            <button className="success-ok-btn" onClick={() => setShowImportSuccess(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Document Save Confirmation Modal */}
      {showDocumentSaveConfirm && (
        <div className="document-save-confirm-overlay" onClick={handleDocumentSaveCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Save</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to save these document changes?</p>
              {uploadedDocumentNames.length > 0 && (
                <div className="uploaded-docs-list">
                  {uploadedDocumentNames.map((docName, index) => (
                    <span key={index} className="uploaded-doc-badge">{docName}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel-button" onClick={handleDocumentSaveCancel}>
                No
              </button>
              <button className="modal-button confirm-button" onClick={handleDocumentSaveConfirm}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Save Success Modal */}
      {showDocumentSuccess && (
        <div className="success-modal-overlay" onClick={() => setShowDocumentSuccess(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Success!</h2>
            <p className="success-message">Document has been updated Successfully!</p>
            {uploadedDocumentNames.length > 0 && (
              <div className="uploaded-docs-success-list">
                {uploadedDocumentNames.map((docName, index) => (
                  <span key={index} className="uploaded-doc-success-badge">{docName}</span>
                ))}
              </div>
            )}
            <button className="success-ok-btn" onClick={() => {
              setShowDocumentSuccess(false);
              setUploadedDocumentNames([]);
            }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Vehicle Documents Modal */}
      {showDocumentsModal && selectedVehicleForDocs && (
        <div className="vehicle-documents-modal-overlay" onClick={() => setShowDocumentsModal(false)}>
          <div className="vehicle-documents-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="vehicle-documents-header">
              <div>
                <h2 className="vehicle-documents-title">Vehicle Documents</h2>
                <p className="vehicle-documents-subtitle">
                  {selectedVehicleForDocs.id}: {selectedVehicleForDocs.vehicleNumber} - {selectedVehicleForDocs.vehicleType}
                </p>
              </div>
              <button className="vehicle-documents-close" onClick={() => setShowDocumentsModal(false)}>
                ✕
              </button>
            </div>

            <div className="vehicle-documents-tabs">
              {['rcBook', 'insurance', 'pollutionCertificate', 'fitnessCertificate', 'permit', 'roadTax'].map((docKey) => (
                <button
                  key={docKey}
                  className={`vehicle-doc-tab ${activeDocTab === docKey ? 'active' : ''}`}
                  onClick={() => setActiveDocTab(docKey)}
                >
                  {getDocumentLabel(docKey)}
                  {documentDetails[docKey]?.status === 'Missing' && <span className="doc-warning-icon">⚠️</span>}
                </button>
              ))}
            </div>

            <div className="vehicle-documents-body">
              <div className="document-preview-section">
                <h3 className="section-title">Document Preview</h3>
                <div className="document-preview-box">
                  {(() => {
                    const preview = documentPreviews[activeDocTab] || selectedVehicleForDocs?.documents?.[activeDocTab]?.preview;
                    const fileName = documentFileNames[activeDocTab] || selectedVehicleForDocs?.documents?.[activeDocTab]?.fileName || '';
                    
                    if (!preview) {
                      return (
                        <div className="doc-preview-placeholder">
                          <div className="doc-preview-icon">📄</div>
                          <p>No document preview available</p>
                        </div>
                      );
                    }
                    
                    // Check if it's a PDF by file name or URL
                    const isPDF = fileName.toLowerCase().endsWith('.pdf') || 
                                 (typeof preview === 'string' && (preview.toLowerCase().includes('.pdf') || preview.toLowerCase().includes('application/pdf')));
                    
                    if (isPDF) {
                      return (
                        <iframe
                          src={preview}
                          className="doc-preview-image"
                          style={{ width: '100%', height: '100%', border: 'none', minHeight: '400px' }}
                          title="Document preview"
                        />
                      );
                    }
                    
                    // For images or data URLs
                    return (
                      <img 
                        src={preview} 
                        alt="Document preview" 
                        className="doc-preview-image" 
                      />
                    );
                  })()}
                </div>
              </div>

              <div className="document-details-section">
                <h3 className="section-title">Document Details</h3>
                <div className="document-detail-field">
                  <label>Document Number</label>
                  <input
                    type="text"
                    className="document-number-input"
                    placeholder="Enter document number"
                    value={(formData?.documents?.[activeDocTab]?.documentNumber) || (selectedVehicleForDocs?.documents?.[activeDocTab]?.documentNumber) || ''}
                    onChange={(e) => {
                      const newDocuments = { ...(formData?.documents || {}) };
                      newDocuments[activeDocTab] = { ...(newDocuments[activeDocTab] || {}), documentNumber: e.target.value };
                      setFormData(prev => ({ ...prev, documents: newDocuments }));
                    }}
                  />
                </div>
                <div className="document-detail-field">
                  <label>Expiry Date</label>
                  <div className="date-input-group">
                    <input
                      type="date"
                      className="document-expiry-input"
                      value={documentDetails[activeDocTab]?.expiryDate || ''}
                      onChange={(e) => {
                        setDocumentDetails(prev => ({
                          ...prev,
                          [activeDocTab]: {
                            ...prev[activeDocTab],
                            expiryDate: e.target.value
                          }
                        }));
                      }}
                    />
                    <span className="calendar-icon">📅</span>
                  </div>
                </div>

                <div className="document-detail-field">
                  <label>Status</label>
                  <span className={`doc-status-badge ${documentDetails[activeDocTab]?.status === 'Valid' ? 'valid' : 'missing'}`}>
                    {documentDetails[activeDocTab]?.status || 'Missing'}
                  </span>
                </div>

                <div className="document-detail-field">
                  <label>Document History</label>
                  <input
                    type="text"
                    className="document-history-input"
                    value={`Last updated: ${documentDetails[activeDocTab]?.lastUpdated || 'N/A'}`}
                    readOnly
                  />
                </div>

                <div className="document-actions">
                  <button className="replace-file-btn" onClick={() => handleDocumentReplace(activeDocTab)}>
                    <span>⬆️</span> {(documentPreviews[activeDocTab] || documentFileNames[activeDocTab] || selectedVehicleForDocs?.documents?.[activeDocTab]?.fileName || selectedVehicleForDocs?.documents?.[activeDocTab]?.preview) ? 'Replace File' : 'Upload File'}
                  </button>
                  <input
                    type="file"
                    id={`doc-replace-${activeDocTab}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    onChange={(e) => handleDocumentFileChange(activeDocTab, e.target.files[0])}
                  />
                  {((documentPreviews[activeDocTab] || documentFileNames[activeDocTab]) || (selectedVehicleForDocs?.documents?.[activeDocTab]?.fileName || selectedVehicleForDocs?.documents?.[activeDocTab]?.preview)) && (
                    <button className="download-file-btn" onClick={() => handleDocumentDownload(activeDocTab)}>
                      <span>⬇️</span> Download
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="vehicle-documents-footer">
              <button className="close-docs-btn" onClick={() => setShowDocumentsModal(false)}>
                Close
              </button>
              <button className="save-docs-btn" onClick={() => {
                // Get list of all changed/replaced document names
                const changedDocs = [];
                Object.keys(documentFileNames).forEach(key => {
                  // Include if file was uploaded (new or replaced)
                  // Also check if preview changed (image replaced)
                  const hasNewFile = documentFileNames[key] && documentFileNames[key] !== originalDocumentFileNames[key];
                  const hasNewPreview = documentPreviews[key] && documentPreviews[key] !== (selectedVehicleForDocs?.documents?.[key]?.preview || null);
                  if (hasNewFile || hasNewPreview) {
                    changedDocs.push(getDocumentLabel(key));
                  }
                });
                // Always show confirmation before saving
                setUploadedDocumentNames(changedDocs);
                setShowDocumentSaveConfirm(true);
              }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sample CSV Modal */}
      {showSampleCSVModal && (
        <div className="add-vehicle-modal-overlay" onClick={() => setShowSampleCSVModal(false)}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header-bar">
              <h2 className="modal-title">Import Vehicles</h2>
              <button className="modal-close-btn" onClick={() => setShowSampleCSVModal(false)}>✕</button>
            </div>

            <div className="modal-body-content" style={{ padding: '20px' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: '1rem', fontWeight: 600, color: '#333', marginBottom: '8px', display: 'block' }}>Sample Excel Format</label>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '16px', textAlign: 'left' }}>
                  Download the sample Excel template to see the required format for importing vehicles.
                  <br />
                  <span style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
                    Note: Fields marked with * are required. Dropdowns are available for Vehicle Type, Status, Transporter, and Assigned Driver. Vehicle ID will be auto-generated if not provided.
                  </span>
                </p>
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button 
                    className="btn-primary" 
                    onClick={handleDownloadSampleCSV}
                    style={{ flex: 1 }}
                  >
                    <span>⬇</span> Download Sample Excel
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={() => setShowSampleCSVModal(false)}>Cancel</button>
              <button className="modal-btn-save" onClick={handleProceedWithImport}>
                Proceed with Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Preview Modal */}
      {showImportModal && (
        <div className="add-vehicle-modal-overlay" onClick={handleImportCancel}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <h2 className="modal-title">Import Vehicles</h2>
              <button className="modal-close-btn" onClick={handleImportCancel}>✕</button>
            </div>

            <div className="modal-body-content">
              <div className="form-content">
                <div className="form-fields-column">
                  <div className="form-field">
                    <label className="form-field-label">Upload CSV</label>
                    <div className="input-with-suffix">
                      <input
                        type="text"
                        className="form-input-field"
                        readOnly
                        placeholder="Choose .csv file"
                        value={importFileName}
                        onClick={() => document.getElementById('import-file-input').click()}
                      />
                      <span className="input-suffix" style={{cursor:'pointer'}} onClick={() => document.getElementById('import-file-input').click()}>📁</span>
                    </div>
                    <p className="document-hint">Use the sample Excel template. Both .csv and .xlsx files are supported.</p>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">Validation Summary (by Vehicle Number)</label>
                    <div className="validation-summary" style={{display:'flex', gap:'12px', flexWrap:'wrap'}}>
                      <span className="status-badge active">{importValidVehicles.length} Valid</span>
                      <span className="status-badge inactive">{importInvalidRows.length} Invalid</span>
                    </div>
                    {importInvalidRows.length > 0 && (
                      <div style={{marginTop:'10px', maxHeight:'160px', overflow:'auto', border:'1px solid #e5e7eb', borderRadius:'6px', padding:'8px'}}>
                        {importInvalidRows.slice(0, 50).map((r, i) => (
                          <div key={i} style={{fontSize:'0.85rem', color:'#6b7280'}}>
                            Row {r.row}: {r.vehicleNumber || '(empty)'} — {r.reason}
                          </div>
                        ))}
                        {importInvalidRows.length > 50 && (
                          <div style={{fontSize:'0.85rem', color:'#6b7280', marginTop:'6px'}}>+ {importInvalidRows.length - 50} more…</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={handleImportCancel}>Cancel</button>
              <button className="modal-btn-save" disabled={importValidVehicles.length === 0} onClick={handleImportConfirm}>
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Success!</h2>
            <p className="success-message">
              Vehicle details have been successfully {successModalType === 'add' ? 'Added' : 'Updated'}
            </p>
            <div className="success-id-badge">{successVehicleNumber}</div>
            <button className="success-ok-btn" onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleManagement;




